import React from 'react';

import { Box, Typography } from '@mui/material';
import _ from 'es-toolkit/compat';

import { TableActionButton, TableSearch } from 'src/components/table';
import type { TableThemes, TableTopAction } from 'src/components/table/types';
import type { SxProp } from 'src/types/mui';

import { TableStyles } from '../../theme';

import type { TableTopMenuProps } from './types';

export const TableTopMenu: React.FC<TableTopMenuProps> = ({
  tableTitle,
  tableTheme,
  searchProps = {},
  actionProps = {},
  hidden,
  extraTopNode,
  isRightMenu = false,
}) => {
  const {
    hasSearch,
    onSearch,
    onSearchCategorySelect,
    searchStyles,
    searchLabel,
    searchInputSize,
    searchCategories,
  } = searchProps;
  const { actions, hidden: isActionsHidden, filterAction } = actionProps;

  if (hidden) return null;

  return (
    <Box sx={tableTopMenuStyles(tableTheme)}>
      {tableTitle ? (
        <Box margin={3}>
          <Typography variant="h2" color="initial" paddingTop={3}>
            {tableTitle}
          </Typography>
        </Box>
      ) : null}

      <Box sx={menuContainerStyles(isRightMenu)}>
        {extraTopNode ? <Box>{extraTopNode}</Box> : null}
        {(hasSearch || (!isActionsHidden && filterAction)) && (
          <Box>
            {hasSearch ? (
              <TableSearch
                onSearch={onSearch}
                onCategorySelect={onSearchCategorySelect}
                searchStyles={searchStyles}
                label={searchLabel}
                size={searchInputSize}
                searchCategories={searchCategories}
              />
            ) : null}
            {!isActionsHidden && filterAction && <Box>{renderTopAction(filterAction)}</Box>}
          </Box>
        )}
        {!isActionsHidden && !!actions?.length && <Box>{actions?.map(renderTopAction)}</Box>}
      </Box>
    </Box>
  );
};

const renderTopAction = (action: TableTopAction): React.ReactNode => {
  if (action.render && _.isFunction(action.render)) {
    return action.render();
  }

  return (
    <TableActionButton
      key={action.actionName}
      icon={action.icon}
      onClick={action.onAction}
      {...action.buttonProps}
    >
      {action.actionName}
    </TableActionButton>
  );
};

const menuContainerStyles =
  (isRightMenu: boolean): SxProp =>
  theme => ({
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 2,
    width: '100%',
    '& > *': {
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'nowrap',
      alignItems: 'center',
      width: '100%',
      gap: 2,

      '&:last-of-type': {
        justifyContent: 'flex-end',
      },

      '&:only-of-type': {
        justifyContent: isRightMenu ? 'flex-end' : 'flex-start',
      },
    },

    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: 3,
    },
  });

const tableTopMenuStyles =
  (tableTheme: TableThemes): SxProp =>
  theme => ({
    marginRight: 'auto',
    padding: TableStyles[tableTheme].tableTopMenuPadding,
    width: '100%',

    [theme.breakpoints.down('sm')]: {
      padding: '16px 16px 24px 16px',
    },
  });
